import React, { useState, useEffect } from 'react';
import { dataService } from '../services/dataService';

const STATUS_META = {
  completed: { label: 'Completed', bg: '#dcfce7', color: '#16a34a', stripe: '#22c55e' },
  failed:    { label: 'Failed',    bg: '#fee2e2', color: '#dc2626', stripe: '#ef4444' },
  paused:    { label: 'Paused',    bg: '#fef3c7', color: '#d97706', stripe: '#f59e0b' },
};

const STEP_STATUS_META = {
  ok:     { label: 'OK',      bg: '#dcfce7', color: '#16a34a', icon: '●' },
  error:  { label: 'Error',   bg: '#fee2e2', color: '#dc2626', icon: '●' },
  paused: { label: 'Paused',  bg: '#fef3c7', color: '#d97706', icon: '●' },
};

const TRIGGER_LABELS = {
  manual:              'Manual',
  record_added:        'Record added',
  field_change:        'Field changed',
  function_task_change:'Task changed',
  schedule:            'Schedule',
  poll_api:            'Poll API',
};

function formatTs(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return `${d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
}

function formatDuration(ms) {
  if (!ms && ms !== 0) return '';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
}

function getStepExplanation(step) {
  const { nodeType, status, detail } = step;
  const d = (detail || '').toLowerCase();

  if (status === 'ok') {
    if (nodeType === 'triggerNode') return 'This trigger fired successfully and started the workflow. The conditions to start were met.';
    if (nodeType === 'conditionNode') {
      if (d.includes('yes')) return 'The condition was evaluated and the result was YES — the workflow continued down the "true" branch.';
      return 'The condition was evaluated and the result was NO — the workflow continued down the "false" branch.';
    }
    if (nodeType === 'actionNode') {
      if (d.includes('update_field')) return 'The field update action ran successfully. The target record(s) in the connected collection(s) were updated with the configured value.';
      if (d.includes('send_notification')) return 'A notification was sent to the user\'s inbox successfully.';
      if (d.includes('http_request')) return 'The HTTP request was sent to the external service and received a successful response (2xx status code).';
      if (d.includes('create_record')) return 'A new record was created in the connected collection with the configured default values.';
      if (d.includes('create_function_task')) return 'A new task was added to the connected function node\'s task queue.';
      if (d.includes('update_function_task')) return 'An existing task in the function node was updated with the new value.';
      return 'The action executed successfully and produced the expected result.';
    }
    if (nodeType === 'functionNode') return 'A task was created in this function\'s queue. A team member or agent should pick it up and process it.';
    if (nodeType === 'gateNode') return 'The gate was passed — approval was granted and the workflow continued.';
    if (d.includes('pass-through')) return 'This node was traversed as part of the workflow path. No action was needed — it served as a waypoint.';
    return 'This step completed successfully with no issues.';
  }

  if (status === 'error') {
    if (d.includes('not connected to a collection')) return 'The action node has no connector (dotted line) going to a data collection. Connect the action to the collection it should update by drawing an edge from the action to the collection node.';
    if (d.includes('no field')) return 'The action tried to update a field that doesn\'t exist in the target collection. Check that the field name in the action configuration matches a field in the connected collection.';
    if (d.includes('not a valid option')) return 'The action tried to set a dropdown/select field to a value that isn\'t in the allowed options list. Check the field\'s options in the collection and make sure the action\'s value matches one of them exactly.';
    if (d.includes('http') && d.includes('4')) return 'The external service rejected the request. Common causes: invalid API key or token, wrong endpoint URL, or the service requires different request format. Check the connection node\'s auth settings.';
    if (d.includes('http') && d.includes('5')) return 'The external service experienced an internal error. This is usually temporary — try again in a few minutes. If it persists, the service may be down or misconfigured.';
    if (d.includes('http') && d.includes('url')) return 'No URL was configured for the HTTP request. Make sure the action is connected to a Connection node with a valid service URL, or set the endpoint directly on the action.';
    if (d.includes('network') || d.includes('fetch')) return 'A network error occurred when trying to reach the external service. The service may be unreachable, the URL may be incorrect, or there may be a firewall/DNS issue.';
    if (d.includes('failed for all targets')) return 'The action was connected to one or more collections but couldn\'t update any of them. Each target had a specific issue — expand the detail to see which collections failed and why.';
    if (d.includes('timeout')) return 'The operation took too long and was automatically cancelled. This can happen with slow external APIs or very large data operations.';
    return 'This step failed during execution. Check the error detail for the specific reason. Common causes include missing connections, misconfigured fields, or unreachable external services.';
  }

  if (status === 'paused') {
    return 'The workflow is paused at this gate and waiting for manual approval. Check your inbox for the approval request, or approve/reject directly from the gate node on the pipeline canvas.';
  }

  return 'No additional information available for this step.';
}

function getRunExplanation(run) {
  if (run.status === 'completed') {
    return `This workflow ran successfully from start to finish. All ${(run.steps || []).length} steps completed without errors. Triggered by: ${TRIGGER_LABELS[run.triggerType] || run.triggerType}.`;
  }
  if (run.status === 'failed') {
    const errorStep = (run.steps || []).find(s => s.status === 'error');
    if (errorStep) {
      return `This workflow failed at the "${errorStep.nodeLabel}" step. The error stopped the workflow — steps after the failed node were not executed. See the step details below for the specific error and how to fix it.`;
    }
    return 'This workflow encountered an error and stopped. Expand the run to see which step failed and why.';
  }
  if (run.status === 'paused') {
    return 'This workflow is currently paused at an approval gate. It will resume automatically once the gate is approved, or be cancelled if rejected. Check your inbox for the approval request.';
  }
  return '';
}

function InfoTooltip({ text, show, onToggle }) {
  return (
    <span className="logs-info-wrap">
      <button className="logs-info-btn" onClick={e => { e.stopPropagation(); onToggle(); }} title="More info">i</button>
      {show && (
        <>
          <div className="logs-info-backdrop" onClick={e => { e.stopPropagation(); onToggle(); }} />
          <div className="logs-info-popup" onClick={e => e.stopPropagation()}>
            {text}
          </div>
        </>
      )}
    </span>
  );
}

export default function LogsView({ workspaceId }) {
  const [logs, setLogs] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [triggerFilter, setTriggerFilter] = useState('all');
  const [refFilter, setRefFilter] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [infoId, setInfoId] = useState(null);

  useEffect(() => {
    if (!workspaceId) return;
    return dataService.subscribePipelineLogs(workspaceId, setLogs);
  }, [workspaceId]);

  const triggerTypes = [...new Set(logs.map(l => l.triggerType).filter(Boolean))];
  const anyExternal = logs.some(l => l.externalRef?.id);

  const filtered = logs.filter(l => {
    if (statusFilter !== 'all' && l.status !== statusFilter) return false;
    if (triggerFilter !== 'all' && l.triggerType !== triggerFilter) return false;
    if (refFilter.trim()) {
      // "What has happened on ticket X?" — match the id the other app knows the
      // record by, falling back to the local record id for ordinary collections.
      const needle = refFilter.trim().toLowerCase();
      const hay = `${l.externalRef?.id || ''} ${l.recordId || ''}`.toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    return true;
  });

  if (!workspaceId) {
    return (
      <div className="logs-wrap">
        <div className="logs-empty">
          <div className="logs-empty-icon">📋</div>
          <div>Select a workspace to view pipeline logs.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="logs-wrap">
      <div className="logs-header">
        <div className="logs-header-left">
          <h2 className="logs-title">Pipeline Logs</h2>
          <span className="logs-count">{filtered.length} run{filtered.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="logs-controls">
          {anyExternal && (
            <input
              className="logs-select logs-ref-search"
              type="search"
              placeholder="Find by ticket id…"
              value={refFilter}
              onChange={e => setRefFilter(e.target.value)}
              title="Show every run that acted on one record in the connected app"
            />
          )}
          <select className="logs-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="all">All statuses</option>
            {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select className="logs-select" value={triggerFilter} onChange={e => setTriggerFilter(e.target.value)}>
            <option value="all">All triggers</option>
            {triggerTypes.map(t => <option key={t} value={t}>{TRIGGER_LABELS[t] || t}</option>)}
          </select>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="logs-empty">
          <div className="logs-empty-icon">📋</div>
          <div>No pipeline runs in <strong>this workspace</strong> yet. Logs appear here as workflows execute.</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8, maxWidth: 360 }}>
            Logs are per-workspace — switch workspaces (top-left) to view another. The Test App runs in its own “🧪 Test Sandbox” workspace, so its runs show under that one.
          </div>
        </div>
      ) : (
        <div className="logs-runs-list">
          {filtered.map(run => {
            const meta = STATUS_META[run.status] || { label: run.status, bg: '#f1f5f9', color: '#64748b', stripe: '#94a3b8' };
            const expanded = expandedId === run.id;
            const steps = run.steps || [];
            const errorStep = steps.find(s => s.status === 'error');

            return (
              <div key={run.id} className={`logs-run-card ${run.status || 'unknown'}${expanded ? ' expanded' : ''}`}
                   style={{ borderLeftColor: meta.stripe }}>
                <div className="logs-run-summary" onClick={() => setExpandedId(expanded ? null : run.id)}>
                  <div className="logs-run-left">
                    <span className="logs-run-expand">{expanded ? '▾' : '▸'}</span>
                    <span className="logs-run-status-dot" style={{ background: meta.stripe }} />
                    <span className="logs-type-chip" style={{ background: meta.bg, color: meta.color }}>{meta.label}</span>
                    <span className="logs-run-trigger">{run.triggerLabel || 'Workflow'}</span>
                    <span className="logs-run-type-tag">{TRIGGER_LABELS[run.triggerType] || run.triggerType}</span>
                    <InfoTooltip
                      text={getRunExplanation(run)}
                      show={infoId === `run-${run.id}`}
                      onToggle={() => setInfoId(infoId === `run-${run.id}` ? null : `run-${run.id}`)}
                    />
                  </div>
                  <div className="logs-run-right">
                    <span className="logs-run-steps-count">{steps.length} step{steps.length !== 1 ? 's' : ''}</span>
                    {run.durationMs != null && (
                      <span className="logs-run-duration">{formatDuration(run.durationMs)}</span>
                    )}
                    <span className="logs-run-time">{formatTs(run.startedAt)}</span>
                  </div>
                </div>

                {errorStep && !expanded && (
                  <div className="logs-run-error-preview">
                    <span className="logs-run-error-icon">!</span>
                    {errorStep.nodeLabel}: {errorStep.detail}
                  </div>
                )}

                {expanded && steps.length > 0 && (
                  <div className="logs-run-steps">
                    <table className="logs-steps-table">
                      <thead>
                        <tr>
                          <th className="logs-th" style={{ width: 28 }}></th>
                          <th className="logs-th">Node</th>
                          <th className="logs-th">Status</th>
                          <th className="logs-th logs-th-detail">Detail</th>
                          <th className="logs-th">Duration</th>
                          <th className="logs-th" style={{ width: 32 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {steps.map((step, i) => {
                          const sm = STEP_STATUS_META[step.status] || { label: step.status, bg: '#f1f5f9', color: '#64748b', icon: '●' };
                          return (
                            <tr key={i} className={`logs-step-row ${step.status || ''}`}>
                              <td className="logs-td logs-td-dot">
                                <span className="logs-step-dot" style={{ color: sm.color }}>{sm.icon}</span>
                              </td>
                              <td className="logs-td logs-td-node">{step.nodeLabel || '—'}</td>
                              <td className="logs-td">
                                <span className="logs-type-chip" style={{ background: sm.bg, color: sm.color }}>{sm.label}</span>
                              </td>
                              <td className="logs-td logs-td-detail">{step.detail}</td>
                              <td className="logs-td logs-td-dur">{step.durationMs != null ? formatDuration(step.durationMs) : '—'}</td>
                              <td className="logs-td logs-td-info">
                                <InfoTooltip
                                  text={getStepExplanation(step)}
                                  show={infoId === `step-${run.id}-${i}`}
                                  onToggle={() => setInfoId(infoId === `step-${run.id}-${i}` ? null : `step-${run.id}-${i}`)}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <div className="logs-run-meta">
                      <span>Workflow: {run.workflowId}</span>
                      {run.externalRef?.id
                        ? <span>{run.externalRef.system}: {run.externalRef.id}</span>
                        : run.recordId && <span>Record: {run.recordId}</span>}
                      <span>Completed: {formatTs(run.completedAt)}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
